import React from 'react';
import { FiShoppingCart } from 'react-icons/fi';
import {
  FormSection,
  SectionTitle,
  SubSectionTitle,
  FormGrid,
  FormGroup,
  Label,
  Input,
  Select,
  InfoBox,
  HelpText,
  CalculatedValue,
  CalculatedLabel,
  CalculatedAmount
} from '../common/FormStyles';
import { InformalExpenses } from '@/types/application-forms';
import { DocumentUpload, UploadedDocument } from '../common/DocumentUpload';
import { Candidate } from '@/models';

interface InformalExpensesSectionProps {
  data: Partial<Candidate>;
  onChange: (data: Partial<Candidate>) => void;
  readonly?: boolean;

  uploadedDocuments?: UploadedDocument[];
       onDocumentUpload?: (type: string, file: File | null) => void;
  onDocumentRemove?: (type: string) => void;
}

export const InformalExpensesSection: React.FC<InformalExpensesSectionProps> = ({
  data,
  onChange,
  readonly = false,
  uploadedDocuments = [],
  onDocumentUpload,
  onDocumentRemove,
}) => {
  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const calculateTotalDepenses = () => {
    const {
      loyer_actuel = 0,
      nourriture = 0,
      scolarite_enfants = 0,
      transport = 0,
      sante = 0,
      aide_famille = 0,
      credit_a_rembourser = 0,
      autres_charges = 0
    } = data;

    return (
      Number(loyer_actuel || 0) +
      Number(nourriture || 0) +
      Number(scolarite_enfants || 0) +
      Number(transport || 0) +
      Number(sante || 0) +
      Number(aide_famille || 0) +
      Number(credit_a_rembourser || 0) +
      Number(autres_charges || 0)
    );
  };

  const totalDepenses = calculateTotalDepenses();
  const resteVivre = Number(data.revenu_mensuel_ref || 0) - totalDepenses;

  React.useEffect(() => {
    if (data.total_charges !== totalDepenses) {
      handleChange('total_charges', totalDepenses);
    }
  }, [totalDepenses]);

  React.useEffect(() => {
    if (data.reste_vivre !== resteVivre) {
      handleChange('reste_vivre', resteVivre);
    }
  }, [resteVivre]);

  return (
    <FormSection>
      <SectionTitle>
        <FiShoppingCart />
        Vos Charges Actuelles
      </SectionTitle>

      <InfoBox $type="info">
        <p>
          Détaillez vos dépenses mensuelles pour évaluer votre capacité à payer un loyer.
          Soyez honnête et réaliste dans vos estimations.
        </p>
      </InfoBox>

      <SubSectionTitle>Situation de logement actuelle</SubSectionTitle>

      <FormGroup>
        <Label $required>Votre situation de logement actuel</Label>
        <Select
          value={data.situation_logement || ''}
          onChange={e => handleChange('situation_logement', e.target.value)}
          disabled={readonly}
        >
          <option value="">Sélectionnez...</option>
              <option value="Locataire">Locataire</option>
              <option value="Hébergé famille">Hébergé par la famille</option>
              <option value="Propriétaire">Propriétaire</option>
              <option value="Logement précaire">Logement précaire</option>
              <option value="Logement fonction">Logement de fonction</option>
              <option value="Cour commune">Cour commune</option>
        </Select>
      </FormGroup>

      {data.situation_logement === 'Locataire' && (
        <FormGroup>
          <Label>Loyer mensuel actuel (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.loyer_actuel || ''}
            onChange={e => handleChange('loyer_actuel', parseInt(e.target.value) || 0)}
            placeholder="Ex: 80000"
            disabled={readonly}
          />
        </FormGroup>
      )}

      <SubSectionTitle>Dépenses mensuelles</SubSectionTitle>

      <FormGrid>
        <FormGroup>
          <Label>Nourriture pour la famille (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.nourriture || ''}
            onChange={e => handleChange('nourriture', parseInt(e.target.value) || 0)}
            placeholder="Ex: 100000"
            disabled={readonly}
          />
          <HelpText>Estimation mensuelle pour nourrir toute la famille</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>École des enfants (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.scolarite_enfants || ''}
            onChange={e => handleChange('scolarite_enfants', parseInt(e.target.value) || 0)}
            placeholder="Ex: 30000"
            disabled={readonly}
          />
          <HelpText>Frais scolaires mensuels (ramené au mois si annuel)</HelpText>
        </FormGroup>
      </FormGrid>

      <FormGrid>
        <FormGroup>
          <Label>Transport (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.transport || ''}
            onChange={e => handleChange('transport', parseInt(e.target.value) || 0)}
            placeholder="Ex: 25000"
            disabled={readonly}
          />
          <HelpText>Déplacements pour le travail et la famille</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>Santé (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.sante || ''}
            onChange={e => handleChange('sante', parseInt(e.target.value) || 0)}
            placeholder="Ex: 15000"
            disabled={readonly}
          />
          <HelpText>Médicaments, consultations, etc.</HelpText>
        </FormGroup>
      </FormGrid>

      <FormGrid>
        <FormGroup>
          <Label>Aide à la famille élargie (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.aide_famille || ''}
            onChange={e => handleChange('aide_famille', parseInt(e.target.value) || 0)}
            placeholder="Ex: 20000"
            disabled={readonly}
          />
          <HelpText>Soutien aux parents, frères, sœurs, etc.</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>Crédit à rembourser (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.credit_a_rembourser || ''}
            onChange={e => handleChange('credit_a_rembourser', parseInt(e.target.value) || 0)}
            placeholder="Ex: 10000"
            disabled={readonly}
          />
          <HelpText>Mensualités de crédits en cours</HelpText>
        </FormGroup>
      </FormGrid>

      <FormGroup>
        <Label>Autres dépenses (FCFA)</Label>
        <Input
          type="number"
          min="0"
          value={data.autres_charges || ''}
          onChange={e => handleChange('autres_charges', parseInt(e.target.value) || 0)}
          placeholder="Ex: 5000"
          disabled={readonly}
        />
        <HelpText>Autres charges mensuelles non listées ci-dessus</HelpText>
      </FormGroup>

      <CalculatedValue>
        <CalculatedLabel>Total dépenses mensuelles</CalculatedLabel>
        <CalculatedAmount>{totalDepenses.toLocaleString()} FCFA</CalculatedAmount>
      </CalculatedValue>

      {data.revenu_mensuel_ref && data.revenu_mensuel_ref > 0 && (
        <>
          <CalculatedValue>
            <CalculatedLabel>Revenu mensuel (référence)</CalculatedLabel>
            <CalculatedAmount>{data.revenu_mensuel_ref.toLocaleString()} FCFA</CalculatedAmount>
          </CalculatedValue>

          <CalculatedValue>
            <CalculatedLabel>Reste pour vivre</CalculatedLabel>
            <CalculatedAmount
              style={{ color: resteVivre >= 0 ? '#10b981' : '#ef4444' }}
            >
              {resteVivre.toLocaleString()} FCFA
            </CalculatedAmount>
          </CalculatedValue>

          {resteVivre < 0 && (
            <InfoBox $type="warning">
              <p>
                <strong>Attention :</strong> Vos dépenses dépassent vos revenus déclarés.
                Vérifiez vos estimations ou ajustez vos dépenses pour qu'elles correspondent
                à votre réalité.
              </p>
            </InfoBox>
          )}

          {resteVivre >= 0 && resteVivre < 50000 && (
            <InfoBox $type="warning">
              <p>
                Votre reste pour vivre est très serré. Assurez-vous que vous pourrez payer
                le loyer en plus de vos autres charges.
              </p>
            </InfoBox>
          )}

          {resteVivre >= 50000 && (
            <InfoBox $type="info">
              <p>
                <strong>Bon !</strong> Votre situation financière semble équilibrée avec un
                reste pour vivre de {resteVivre.toLocaleString()} FCFA par mois.
              </p>
            </InfoBox>
          )}
        </>
      )}

      <InfoBox $type="info">
        <p>
          <strong>Conseil :</strong> Le loyer ne devrait pas dépasser 30-40% de vos revenus
          mensuels pour maintenir un équilibre financier sain.
        </p>
      </InfoBox>


{/* 
       {onDocumentUpload && onDocumentRemove && (
                              <DocumentUpload
                                sectionTitle="Vos Clients"
                                documents={INFORMAL_CLIENTS_DOCUMENTS}
                                uploadedFiles={uploadedDocuments}
                                onFileSelect={onDocumentUpload}
                                onFileRemove={onDocumentRemove}
                                readonly={readonly}
                              />
                            )} */}
    </FormSection>
  );
};
